import { Modal, useScreenDetect } from '@sgcmpteltd/uikit-next';
import { Button, IosClose, StarRating } from '@sgcmpteltd/uikit-react';
import {
  BUTTON_COLOR_GRAY,
  BUTTON_COLOR_RED,
  BUTTON_SIZE_SM,
  BUTTON_VARIANT_CONTAINED,
  BUTTON_VARIANT_OUTLINED,
} from '@sgcmpteltd/uikit-react/components/button';
import {
  Result,
  RESULT_VARIANT_WARNING,
} from '@sgcmpteltd/uikit-react/components/result';
import type {
  RatingValue,
} from '@sgcmpteltd/uikit-react/components/star-rating';
import { isRatingValue } from '@sgcmpteltd/uikit-react/components/star-rating';
import cx from 'clsx';
import Cookies from 'js-cookie';
import Link from 'next/link';
import type { MouseEventHandler } from 'react';
import { useContext, useEffect, useState } from 'react';

import type {
  CamelCaseAdAlert,
  CamelCaseCarAlert,
} from '@/apis/server/new-car/v1/my-alert';
import type {
  AdAlertModalContent,
  CarAlertModalContent,
} from '@/app/(routes)/listing/_components/page-content/alert-modal';
import {
  AlertModal,
} from '@/app/(routes)/listing/_components/page-content/alert-modal';
import {
  NUM_NON_LOGIN_SHORTLIST_MAX_VEHICLES,
// eslint-disable-next-line @stylistic/max-len
} from '@/app/(routes)/listing/_components/page-content/search-list/car-card/constants';
import type { User } from '@/app/types';
import { ImageWithFallback } from '@/components/image-with-fallback';
import {
  COOKIE_KEY_MAIL,
  COOKIE_KEY_NON_LOGIN_SHORTLIST,
  COOKIE_KEY_SESSION_ID,
  COOKIE_KEY_USER_ID,
} from '@/configs/cookies';
import { SGCM_SITE_DOMAIN } from '@/configs/public-url';
import { UserContext } from '@/context/user';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';
import { joinSgcmMsSite, joinSgcmSite } from '@/utils/url/join/public';

import styles from './styles.module.scss';
import type { Component } from './types';

const isBrowser = () => typeof window !== 'undefined';

export const CarItem: Component = ({
  index,
  item,
  alertList,
  onChange,
  onRemove,
  onAlertModalSubmit,
}) => {
  const userContext = useContext(UserContext);
  const { isDesktop } = useScreenDetect();
  const isDesktopMode = isDesktop();
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [isShortlisted, setIsShortlisted] = useState(false);
  const user: User = {
    email: Cookies.get(COOKIE_KEY_MAIL) || '',
    userId: Number(Cookies.get(COOKIE_KEY_USER_ID)) || 0,
    sessionId: Cookies.get(COOKIE_KEY_SESSION_ID) || '',
  };

  const ratingValue = isRatingValue(item?.review?.averageRating)
    ? item?.review?.averageRating
    : null;

  const handleChangeClick = () => {
    onChange?.(index);
  };

  const handleRemoveClick = () => {
    onRemove?.(index);
  };

  const handleAlertModalSubmit = (
    alerts: CamelCaseAdAlert | CamelCaseCarAlert,
  ) => {
    onAlertModalSubmit?.(alerts);
  };

  const { adAlert, carAlert } = alertList;

  const carAlertModalContent: CarAlertModalContent = {
    carInfo: {
      id: item?.id ?? 0,
      make: item?.make ?? '',
      model: item?.model ?? '',
    },
    carAlert,
  };
  const adAlertModalContent: AdAlertModalContent = {
    adInfo: {
      id: item?.authorizeDealers ? item?.authorizeDealers[0]?.id : 0,
      name: item?.authorizeDealers ? item?.authorizeDealers[0].name : '',
      brand: item?.make ?? '',
    },
    adAlert,
  };

  useEffect(() => {
    setIsShortlisted((userContext?.userId
      ? (carAlert.adCarPriceChange || carAlert.piCarPromo)
      : Cookies.get(COOKIE_KEY_NON_LOGIN_SHORTLIST)?.split(',')
        .includes(item?.id.toString() ?? '') ?? false));
  }, [carAlert, index, item?.id, userContext?.userId]);

  const rawUrl = isBrowser() ? window.location.href : '';
  const utf8Bytes = new TextEncoder().encode(rawUrl);
  const currentUrlInBase64 = Buffer.from(utf8Bytes).toString('base64');

  const handleShortlistClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    const cookId = Cookies.get(COOKIE_KEY_SESSION_ID);
    const shortlist = Cookies.get(COOKIE_KEY_NON_LOGIN_SHORTLIST);

    if (cookId) {
      setIsAlertModalOpen(true);
    } else if (isDesktopMode) {
      window.open(
        joinSgcmMsSite(`/member/login?referrer=${currentUrlInBase64}`),
        '_self',
        'noreferrer noopener',
      );
    } else if (isShortlisted) {
      setIsShortlisted(false);
      Cookies.set(
        COOKIE_KEY_NON_LOGIN_SHORTLIST,
        shortlist
          ? shortlist.split(',')
            .filter(
              shortlistedId => shortlistedId !== item?.id.toString(),
            )
            .join(',')
          : '',
        {
          domain: process.env.NODE_ENV !== 'development'
            ? SGCM_SITE_DOMAIN
            : undefined,
        },
      );
    } else if (shortlist) {
      if (shortlist.split(',').length >= NUM_NON_LOGIN_SHORTLIST_MAX_VEHICLES) {
        setIsWarningModalOpen(true);
      } else {
        Cookies.set(
          COOKIE_KEY_NON_LOGIN_SHORTLIST,
          `${shortlist},${item?.id}`,
          {
            domain: process.env.NODE_ENV !== 'development'
              ? SGCM_SITE_DOMAIN
              : undefined,
          },
        );
        setIsShortlisted(true);
      }
    } else {
      Cookies.set(
        COOKIE_KEY_NON_LOGIN_SHORTLIST,
        item?.id.toString() ?? '',
        {
          domain: process.env.NODE_ENV !== 'development'
            ? SGCM_SITE_DOMAIN
            : undefined,
        },
      );
      setIsShortlisted(true);
    }
  };

  useBodyScrollLock({ isLocked: isWarningModalOpen });

  return (
    <div
      className={cx(
        styles.containerScrollCell,
        styles.containerCarItemMobile,
      )}>
      <button
        aria-label="Remove"
        className={styles.iconClose}
        type="button"
        onClick={handleRemoveClick}>
        <IosClose
          size={10} />
      </button>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          rowGap: '14px',
          height: '100%',
        }}>
        <Link
          href={joinSgcmMsSite(`/new-cars/info/${item?.id}/${item?.slug}`)}
          prefetch={false}
          style={{ color: '#000' }}>
          {item?.image && (
            <ImageWithFallback
              alt="car-image"
              className={styles.imgCarItem}
              height={80}
              src={`https://i.i-sgcm.com/new_cars/cars/${item?.id}/${item?.id}_m.jpg`}
              width={120} />
          )}
          <div
            className={`${styles.lineClamp}`}
            style={{
              marginTop: 14,
              fontSize: 14,
              lineHeight: '18px',
              fontWeight: 600,
            }}>
            {`${item?.make} ${item?.model}`}
          </div>
        </Link>
        <div className={styles.containerLowerHalfCarItem}>
          <div className={styles.containerStarRating}>
            {item?.review
              ? (
                <>
                  <StarRating
                    iconSize={15}
                    value={(ratingValue ?? 0) as RatingValue} />
                  <span>
                    (
                    {item?.review.count}
                    )
                  </span>
                </>
              )
              : (
                <span
                  className={styles.textNoRatingsYet}>
                  No Ratings yet
                </span>
              )}
          </div>
          <Button
            className={styles.btnShortlist}
            color={BUTTON_COLOR_RED}
            size={BUTTON_SIZE_SM}
            variant={isShortlisted
              ? BUTTON_VARIANT_CONTAINED
              : BUTTON_VARIANT_OUTLINED}
            onClick={handleShortlistClick}>
            {isShortlisted ? 'Shortlisted' : 'Shortlist'}
          </Button>
          <Button
            className={styles.btnChangeCar}
            color={BUTTON_COLOR_GRAY}
            size={BUTTON_SIZE_SM}
            variant={BUTTON_VARIANT_OUTLINED}
            onClick={handleChangeClick}>
            Change Car
          </Button>
        </div>
      </div>
      <AlertModal
        adAlertModalContent={adAlertModalContent}
        carAlertModalContent={carAlertModalContent}
        isOpen={isAlertModalOpen}
        type={item?.authorizeDealers === null ? 'piAlert' : 'carAlert'}
        user={user}
        onClose={() => {
          setIsAlertModalOpen(false);
        }}
        onSubmit={handleAlertModalSubmit} />
      <Modal
        className={cx(
          styles.containerModal,
          styles.isSmall,
        )}
        isOpen={isWarningModalOpen}
        title={null}
        isBackdropStatic
        onClose={() => setIsWarningModalOpen(false)}>
        <Result
          className={styles.containerResult}
          message={(
            <>
              <span className={styles.textWarningMessage}>
                You have reached the maximum of
                {' '}
                {NUM_NON_LOGIN_SHORTLIST_MAX_VEHICLES}
                {' '}
                vehicles.
              </span>
              <span className={cx(
                styles.textWarningMessage,
                styles.isNote,
              )}>
                Please
                {' '}
                <Link href={`${joinSgcmSite('/member/login')}?referrer=${currentUrlInBase64}`}>login</Link>
                {' '}
                to shortlist more.
              </span>
            </>
          )}
          title="Limit Reached"
          variant={RESULT_VARIANT_WARNING} />
      </Modal>
    </div>
  );
};
